import Dropdown from "../../../ui/Dropdown";
import UserIcon from "../../../ui/icons/UserIcon";
import FlagIcon from "../../../ui/icons/FlagIcon";
import ToggleOffIcon from "../../../ui/icons/ToggleOffIcon";
import ToggleOnIcon from "../../../ui/icons/ToggleOnIcon";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { clientErrorMessages, N_A_STRING, SUCCESS } from "../../../constants";
import useAutoDismissMessage from "../../../hooks/useAutoDismissMessage";
import { extractDateOnly, getPlan } from "../../CreateTPlan_1Upload/helpers";
import {
    updateClinician,
    filterAvendaHelperAccountsForAssignedClinician,
} from "./helpers";
import { updateTreatmentPlan } from "../../../helpers/backend_api";
import ClockIcon from "../../../ui/icons/ClockIcon";
import { useDispatch, useSelector } from "react-redux";
import { planStageEnum } from "../RequirementsModal/constants";
import { setCurrentTreatmentPlan } from "../../CreateTPlan_1Upload/actions";
import { setErrorState } from "../../../redux/error_banner/actions";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { isAnyCorePositiveCheck } from "../../CreateTPlan_3Biopsy/helpers";
import { getBxTemplate } from "../../../helpers/tplan/tplan";
import {
    NEEDS_REVIEW_TOGGLE_TOOLTIP_MSG,
    NO_PHYSICIAN,
    PATIENT_INFO_HEADER,
    PLAN_DETAILS_PLACEHOLDERS,
    PLAN_DETAILS_HEADERS,
    TIME_OUT_DURATION_MS,
    PLAN_DETAILS_SUBHEADERS,
    PATHOLOGY_REPORT_TOOLTIP_PROMPTS,
    USER_ID,
    AVENDA_EMAIL_REGEX_PATTERN,
    CHANGE_BIOPSY_TEMPLATE_PROMPT,
    INELIGIBLE_TOGGLE_TOOLTIP_MSG,
} from "./constants";
import { trackingEventNames } from "../../../helpers/pendo_constants";
import { SystematicTemplateOptions } from "../../CreateTPlan_3Biopsy/constants";
import Alert from "../Alert";
import CopyIcon from "../../../ui/icons/CopyIcon";
import DecisionAlertCard from "../DecisionAlertCard";

function PlanDetailsTab(props) {
    const dispatch = useDispatch();
    const handleSetCurrentTreatmentPlan = (plan) => {
        dispatch(setCurrentTreatmentPlan(plan));
    };

    const currentTreatmentPlan = useSelector(
        (state) => state.CreatePlanReducer.currentTreatmentPlan
    );
    const isFreehandPlan = useSelector(
        (state) => state.CreatePlanReducer.isFreehandPlan
    );

    const metadata = currentTreatmentPlan.MetaData || {};
    const savedData = currentTreatmentPlan.SavedData || {};
    const treatmentPlan = currentTreatmentPlan.TreatmentPlan || {};
    const patientData = treatmentPlan.PatientData || {};
    const headerData = treatmentPlan.HeaderData || {};
    const visitData = extractDateOnly(
        currentTreatmentPlan.MetaData?.DateOfVisit
    );
    const planStatusModifiedDate = metadata.PlanStageModifiedDate;
    const planUUID = metadata.PlanUUID;
    const patientUUID = metadata.PatientUUID;

    const isAvendaUser = !!props.userData?.email?.match(
        AVENDA_EMAIL_REGEX_PATTERN
    );

    const [planStatus, setPlanStatus] = useState(metadata?.PlanStage ?? null);
    const [physician, setPhysician] = useState(
        metadata?.PrimaryPhysicianUUID || NO_PHYSICIAN
    );

    const [isPathEntryBtnDisabled, setIsPathEntryBtnDisabled] = useState(true);
    const [isNeedsReviewBtnDisabled, setIsNeedsReviewBtnDisabled] =
        useState(true);
    const isIneligibleBtnDisabled = planStatus === planStageEnum.CEM_GENERATED;
    const [pathReqAlertMsg, setPathReqAlertMsg] =
        useAutoDismissMessage(TIME_OUT_DURATION_MS);
    const [needsReviewAlertMsg, setNeedsReviewAlertMsg] =
        useAutoDismissMessage(TIME_OUT_DURATION_MS);
    const [ineligibleAlertMsg, setIneligibleAlertMsg] =
        useAutoDismissMessage(TIME_OUT_DURATION_MS);
    const [copied, setCopied] = useState(false);
    const [pendingBiopsyTemplate, setPendingBiopsyTemplate] = useState(null);

    // Sets initial fields based off current plan selector
    useEffect(() => {
        if (metadata.PlanStage !== planStageEnum.NO_STATUS) {
            setPlanStatus(metadata.PlanStage);
        }
        setPhysician(metadata.PrimaryPhysicianUUID);

        const hasPositiveCore = isAnyCorePositiveCheck(currentTreatmentPlan);
        setIsNeedsReviewBtnDisabled(!hasPositiveCore);
        // If pathology data does not meet requirements and toggle is ON, turn if OFF
        if (!hasPositiveCore && planStageEnum.NEEDS_REVIEW === planStatus) {
            handlePlanStatusUpdate(planStageEnum.NO_STATUS);
        }
    }, [currentTreatmentPlan]);

    // Updates primary physician and plan status when plan or physician are updated
    useEffect(() => {
        const isPathEntryRequested =
            planStatus === planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS;
        // Pathology entry services aren't available for freehand (MRI-only) plans. The backend
        // rejects the request too; an already-requested freehand plan can still be cancelled
        const meetsAllPathEntryRequirements =
            !isFreehandPlan &&
            physician !== NO_PHYSICIAN &&
            physician !== null &&
            patientData.PSA !== 0 &&
            savedData?.IsPathReportUploaded &&
            planStatus !== planStageEnum.CEM_GENERATED &&
            planStatus !== planStageEnum.INELIGIBLE;

        setIsPathEntryBtnDisabled(
            !(isPathEntryRequested || meetsAllPathEntryRequirements)
        );
    }, [planStatus, physician]);

    async function handlePathologyEntryRequest(e) {
        e.stopPropagation();

        if (
            isPathEntryBtnDisabled &&
            planStatus !== planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS
        ) {
            return;
        }

        if (planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS === planStatus) {
            handlePlanStatusUpdate(planStageEnum.NO_STATUS);
        } else {
            handlePlanStatusUpdate(planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS);
        }
    }

    function handleNeedsReviewRequest(e) {
        e.stopPropagation();
        if (planStageEnum.NEEDS_REVIEW === planStatus) {
            handlePlanStatusUpdate(planStageEnum.NO_STATUS);
        } else {
            handlePlanStatusUpdate(planStageEnum.NEEDS_REVIEW);
        }
    }

    function handleIneligibleRequest(e) {
        e.stopPropagation();
        if (planStageEnum.INELIGIBLE === planStatus) {
            handlePlanStatusUpdate(planStageEnum.NO_STATUS);
        } else {
            handlePlanStatusUpdate(planStageEnum.INELIGIBLE);
        }
    }

    function handlePlanStatusUpdate(status) {
        const updatePlanStatusRequest = {
            token: props.authToken,
            useruuid: props.userData.uuid,
            patientuuid: patientUUID,
            planuuid: planUUID,
            plan: {
                MetaData: {
                    PlanStage: status,
                },
            },
        };

        const oldPlanStatus = planStatus;

        updateTreatmentPlan(updatePlanStatusRequest)
            .then((response) => response.json())
            .then((json) => {
                if (json.message === SUCCESS) {
                    setPlanStatus(json.payload.plan?.MetaData?.PlanStage);
                    handleSetCurrentTreatmentPlan(json.payload.plan);
                } else {
                    handlePlanStatusUpdateFailure(oldPlanStatus, status);
                }
            })
            .catch(() => handlePlanStatusUpdateFailure(oldPlanStatus, status));
    }

    function handlePlanStatusUpdateFailure(oldPlanStatus, attemptedStatus) {
        // Show the alert on the toggle the user interacted with. Turning a
        // toggle off sends NO_STATUS, so fall back to the status being cleared.
        const toggledStatus =
            attemptedStatus === planStageEnum.NO_STATUS
                ? oldPlanStatus
                : attemptedStatus;

        if (toggledStatus === planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS) {
            setPathReqAlertMsg(
                clientErrorMessages.FAILED_TO_REQUEST_PATHOLOGY_ENTRY
            );
        } else if (toggledStatus === planStageEnum.NEEDS_REVIEW) {
            setNeedsReviewAlertMsg(
                clientErrorMessages.FAILED_TO_MARK_NEEDS_REVIEW
            );
        } else if (toggledStatus === planStageEnum.INELIGIBLE) {
            setIneligibleAlertMsg(
                clientErrorMessages.FAILED_TO_MARK_INELIGIBLE
            );
        }
    }

    async function handlePhysicianUpdate(selectedPhysician) {
        const updatePhysicianRequest = {
            token: props.authToken,
            patientUUID: patientUUID,
            planUUID: planUUID,
            AssignerUserUUID: props.userData.uuid,
            AssigneeUserUUID: selectedPhysician.uuid,
        };

        try {
            await updateClinician(updatePhysicianRequest);
            setPhysician(selectedPhysician.uuid);

            const response = await getPlan(
                props.authToken,
                patientUUID,
                planUUID
            );
            const json = await response.json();
            handleSetCurrentTreatmentPlan(json.payload.plan);
        } catch (error) {
            console.error("Error updating primary physician:", error);
        }
    }

    async function handleRemovePhysician() {
        const removePhysicianRequest = {
            token: props.authToken,
            patientUUID: patientUUID,
            planUUID: planUUID,
            AssignerUserUUID: props.userData.uuid,
            AssigneeUserUUID: NO_PHYSICIAN,
        };

        try {
            await updateClinician(removePhysicianRequest);
            setPhysician(NO_PHYSICIAN);

            const response = await getPlan(
                props.authToken,
                patientUUID,
                planUUID
            );
            const json = await response.json();
            handleSetCurrentTreatmentPlan(json.payload.plan);
        } catch (error) {
            console.error("Error removing primary physician:", error);
        }
    }

    function getPhysicianDropdownItems() {
        // remove all helper accounts
        const filteredList = filterAvendaHelperAccountsForAssignedClinician(
            props.doctorList,
            props.userData
        );
        // Include the current user so a clinician can assign themselves.
        // doctorList excludes the current user (see Sidebar getDoctorsList)
        const currentUser = {
            firstName: props.userData.firstName,
            lastName: props.userData.lastName,
            uuid: props.userData.uuid,
            email: props.userData.email,
        };
        return [currentUser, ...filteredList].map((doctorData) => ({
            value: doctorData.uuid,
            label: `${doctorData.firstName} ${doctorData.lastName}`,
            ...doctorData,
        }));
    }

    function renderPatientInfoSection() {
        return (
            <div className="pendo-ignore">
                <div className="mb-3 text-xl">
                    {patientData.FirstName} {patientData.LastName}
                </div>
                <div className="flex justify-between">
                    <div>
                        <div className="text-sm font-medium">
                            {PATIENT_INFO_HEADER.ID}
                        </div>
                        <div>{patientData.PatientMRN}</div>
                    </div>
                    <div>
                        <div className="text-sm font-medium">
                            {PATIENT_INFO_HEADER.DOB}
                        </div>
                        <div>{patientData.DOB}</div>
                    </div>
                    <div>
                        <div className="text-sm font-medium">
                            {PATIENT_INFO_HEADER.PSA}
                        </div>
                        <div>{patientData.PSA || N_A_STRING}</div>
                    </div>
                </div>
                <hr className="my-4 bg-neutral-50" />
            </div>
        );
    }

    function renderPathologyTooltip(props) {
        return (
            <Tooltip
                className="click-outside-ignore"
                id="sidebar-path-entry-toggle-tooltip"
                {...props}
            >
                {getPathologyTooltipMsg()}
            </Tooltip>
        );
    }

    function getPathologyTooltipMsg() {
        const { PSA: psa } = patientData;
        const { IsPathReportUploaded } = savedData;
        const hasPhysician = physician && physician !== NO_PHYSICIAN;
        const isPSAValid = psa !== 0;
        const isPathEntryRequested =
            planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS === planStatus;

        const missingAllReqs =
            !hasPhysician && !isPSAValid && !IsPathReportUploaded;

        // CANCEL comes first so a freehand request made before this rule can still be cancelled
        if (isPathEntryRequested) {
            return PATHOLOGY_REPORT_TOOLTIP_PROMPTS.CANCEL;
        } else if (planStageEnum.INELIGIBLE === planStatus) {
            return PATHOLOGY_REPORT_TOOLTIP_PROMPTS.INELIGIBLE;
        } else if (isFreehandPlan) {
            return PATHOLOGY_REPORT_TOOLTIP_PROMPTS.FREEHAND;
        } else if (missingAllReqs) {
            return PATHOLOGY_REPORT_TOOLTIP_PROMPTS.MISSING_ALL_REQS.REGULAR;
        } else if (!hasPhysician) {
            return PATHOLOGY_REPORT_TOOLTIP_PROMPTS.MISSING_PRIMARY_PHYSICIAN;
        } else if (!isPSAValid) {
            return PATHOLOGY_REPORT_TOOLTIP_PROMPTS.MISSING_PSA;
        } else if (!IsPathReportUploaded) {
            return PATHOLOGY_REPORT_TOOLTIP_PROMPTS.MISSING_PATHOLOGY;
        }

        return PATHOLOGY_REPORT_TOOLTIP_PROMPTS.DEFAULT;
    }

    function renderNeedsReviewTooltip(props) {
        let tooltipMsg = NEEDS_REVIEW_TOGGLE_TOOLTIP_MSG.DEFAULT;
        if (isNeedsReviewBtnDisabled) {
            tooltipMsg = NEEDS_REVIEW_TOGGLE_TOOLTIP_MSG.MISSING_PATHOLOGY;
        }
        if (planStatus === planStageEnum.NEEDS_REVIEW) {
            tooltipMsg = NEEDS_REVIEW_TOGGLE_TOOLTIP_MSG.CANCEL;
        }

        return (
            <Tooltip
                className="click-outside-ignore"
                id="sidebar-needs-review-toggle-tooltip"
                {...props}
            >
                {tooltipMsg}
            </Tooltip>
        );
    }

    function renderIneligibleTooltip(props) {
        let tooltipMsg = INELIGIBLE_TOGGLE_TOOLTIP_MSG.DEFAULT;
        if (isIneligibleBtnDisabled) {
            tooltipMsg = INELIGIBLE_TOGGLE_TOOLTIP_MSG.GENERATED_ERROR;
        }
        if (planStatus === planStageEnum.INELIGIBLE) {
            tooltipMsg = INELIGIBLE_TOGGLE_TOOLTIP_MSG.CANCEL;
        }

        return (
            <Tooltip
                className="click-outside-ignore"
                id="sidebar-ineligible-toggle-tooltip"
                {...props}
            >
                {tooltipMsg}
            </Tooltip>
        );
    }

    async function handleCopy() {
        const text = [
            `User ID: ${props.userData.uuid}`,
            `Plan ID: ${planUUID}`,
            `Patient ID: ${patientUUID}`,
            `Visit Date: ${visitData}`,
        ].join("\n");

        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), TIME_OUT_DURATION_MS);
        } catch (err) {
            console.error("Failed to copy to clipboard:", err);
        }
    }

    function handleBiopsyTemplateChange(template) {
        setPendingBiopsyTemplate(template);
    }

    async function confirmBiopsyTemplateChange() {
        const template = pendingBiopsyTemplate;
        setPendingBiopsyTemplate(null);

        const clearedCores = treatmentPlan?.StructureData?.Biopsy?.map(
            (core) => ({
                ...core,
                GleasonGradeGroup: 0,
                PrimaryScore: 0,
                SecondaryScore: 0,
                TertiaryScore: 0,
                TissueLength: 0,
                CancerLength: 0,
                PercentCancer: 0,
            })
        );

        const req = {
            token: props.authToken,
            useruuid: props.userData.uuid,
            patientuuid: patientUUID,
            planuuid: planUUID,
            plan: {
                TreatmentPlan: {
                    StructureData: {
                        StructureMetaData: {
                            BiopsyTemplate: template.value,
                        },
                        ...(clearedCores && { Biopsy: clearedCores }),
                    },
                },
            },
        };

        try {
            const response = await updateTreatmentPlan(req);
            const json = await response.json();
            if (json.message !== SUCCESS) {
                throw new Error(json.message);
            }

            handleSetCurrentTreatmentPlan(json.payload.plan);
        } catch (error) {
            dispatch(
                setErrorState(
                    true,
                    trackingEventNames.UPDATE_PLAN,
                    planUUID,
                    clientErrorMessages.UPDATE_BIOPSY_TEMPLATE_FAILED
                )
            );
        }
    }

    return (
        <div className="mx-3 mt-3 rounded-lg p-6 shadow-[0px_0px_5px_0px_rgba(0,0,0,0.1)]">
            {renderPatientInfoSection()}
            <div>
                <div className="mb-1 flex items-center gap-1.5">
                    <UserIcon />
                    <div className="text-xl">
                        {PLAN_DETAILS_HEADERS.ASSIGNED_CLINICIAN}
                    </div>
                </div>
                <Dropdown
                    id="sidebar-primary-physician"
                    placeholder={PLAN_DETAILS_PLACEHOLDERS.SELECT_CLINICIAN}
                    value={physician}
                    dropdownItems={getPhysicianDropdownItems()}
                    onChange={handlePhysicianUpdate}
                    onCancel={handleRemovePhysician}
                    renderDropdownItem={(item) => {
                        return (
                            <>
                                <div>
                                    Dr. {item.firstName} {item.lastName}
                                </div>
                                <div className="truncate">{item.email}</div>
                            </>
                        );
                    }}
                />
                <hr className="my-4 bg-neutral-50" />
            </div>
            <div>
                <div className="h-16">
                    <div className="relative flex w-full justify-between">
                        {pathReqAlertMsg && (
                            <Alert
                                className="absolute -top-12 z-50 w-full items-center p-0.5 text-center text-sm"
                                variant="danger"
                                text={pathReqAlertMsg}
                            />
                        )}
                        <div className="flex items-center gap-1.5">
                            <div className="text-xl">
                                {PLAN_DETAILS_HEADERS.REQ_PATH_ENTRY}
                            </div>
                        </div>
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={renderPathologyTooltip()}
                        >
                            <button
                                id="sidebar-path-entry-toggle"
                                className={`focus:outline-hidden ${isPathEntryBtnDisabled && "cursor-not-allowed"}`}
                                onClick={handlePathologyEntryRequest}
                                disabled={isPathEntryBtnDisabled}
                            >
                                {planStatus ===
                                planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS ? (
                                    <ToggleOnIcon />
                                ) : (
                                    <ToggleOffIcon />
                                )}
                            </button>
                        </OverlayTrigger>
                    </div>
                    <div
                        id="plan-details-path-entry-subheader"
                        className="text-sm"
                    >
                        {planStatus ===
                        planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS
                            ? PLAN_DETAILS_SUBHEADERS.CANCEL_PATH_ENTRY
                            : PLAN_DETAILS_SUBHEADERS.SEND_PATH_ENTRY}
                        {planStatusModifiedDate &&
                            planStatus ===
                                planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS && (
                                <div
                                    id="pathology-entry-requested-date"
                                    className="mt-1 flex items-center gap-1"
                                >
                                    <ClockIcon size="12" />
                                    <span className="text-sm text-neutral-400">
                                        Requested On: {planStatusModifiedDate}
                                    </span>
                                </div>
                            )}
                    </div>
                </div>
                <hr className="my-4 bg-neutral-50" />
                <div className="relative flex w-full justify-between">
                    {needsReviewAlertMsg && (
                        <Alert
                            className="absolute -top-12 z-50 w-full items-center p-0.5 text-center text-sm"
                            variant="danger"
                            text={needsReviewAlertMsg}
                        />
                    )}
                    <div className="flex items-center gap-1.5">
                        <div className="text-xl">
                            {PLAN_DETAILS_HEADERS.REVIEW}
                        </div>
                    </div>
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={renderNeedsReviewTooltip()}
                    >
                        <button
                            id="sidebar-needs-review-toggle"
                            className={`focus:outline-hidden ${isNeedsReviewBtnDisabled && "cursor-not-allowed"}`}
                            onClick={handleNeedsReviewRequest}
                            disabled={isNeedsReviewBtnDisabled}
                        >
                            {planStatus === planStageEnum.NEEDS_REVIEW ? (
                                <ToggleOnIcon />
                            ) : (
                                <ToggleOffIcon />
                            )}
                        </button>
                    </OverlayTrigger>
                </div>
                <div
                    id="plan-details-needs-review-subheader"
                    className="text-sm"
                >
                    {planStageEnum.NEEDS_REVIEW === planStatus
                        ? PLAN_DETAILS_SUBHEADERS.REVIEW_OFF
                        : PLAN_DETAILS_SUBHEADERS.REVIEW_ON}
                </div>
                <hr className="my-4 bg-neutral-50" />
                <div className="relative flex w-full justify-between">
                    {ineligibleAlertMsg && (
                        <Alert
                            className="absolute -top-12 z-50 w-full items-center p-0.5 text-center text-sm"
                            variant="danger"
                            text={ineligibleAlertMsg}
                        />
                    )}
                    <div className="flex items-center gap-1.5">
                        <div
                            id="plan-details-ineligible-header"
                            className="text-xl"
                        >
                            {PLAN_DETAILS_HEADERS.INELIGIBLE}
                        </div>
                    </div>
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={renderIneligibleTooltip()}
                    >
                        <button
                            id="sidebar-ineligible-toggle"
                            className={`focus:outline-hidden ${isIneligibleBtnDisabled && "cursor-not-allowed"}`}
                            onClick={handleIneligibleRequest}
                            disabled={isIneligibleBtnDisabled}
                        >
                            {planStatus === planStageEnum.INELIGIBLE ? (
                                <ToggleOnIcon />
                            ) : (
                                <ToggleOffIcon />
                            )}
                        </button>
                    </OverlayTrigger>
                </div>
                <div id="plan-details-ineligible-subheader" className="text-sm">
                    {planStageEnum.INELIGIBLE === planStatus
                        ? PLAN_DETAILS_SUBHEADERS.INELIGIBLE_OFF
                        : PLAN_DETAILS_SUBHEADERS.INELIGIBLE_ON}
                </div>
                <hr className="my-4 bg-neutral-50" />
                <div className="flex w-full justify-between">
                    <div className="flex items-center gap-1.5">
                        <FlagIcon />
                        <div className="text-xl">
                            {PLAN_DETAILS_HEADERS.QUALITY}
                        </div>
                    </div>
                    <button
                        id="sidebar-quality-flag-toggle"
                        className="focus:outline-hidden"
                        onClick={props.toggleFlag}
                    >
                        {props.hasFlag ? (
                            <ToggleOnIcon customColor="var(--error-500)" />
                        ) : (
                            <ToggleOffIcon />
                        )}
                    </button>
                </div>
                <div
                    id="plan-details-quality-flag-subheader"
                    className="text-sm"
                >
                    {props.hasFlag
                        ? PLAN_DETAILS_SUBHEADERS.FLAG_OFF
                        : PLAN_DETAILS_SUBHEADERS.FLAG_ON}
                </div>
                {props.hasFlag && (
                    <textarea
                        id="plan-details-quality-flag-text-area"
                        className="pendo-ignore tw-rounded-sm tw-border mt-1.5 w-full resize-none overflow-y-scroll border-neutral-400 p-2 placeholder:text-(--avendaGray6) focus:outline-hidden"
                        onBlur={props.handleTextAreaChange}
                        placeholder={PLAN_DETAILS_PLACEHOLDERS.QUALITY_FLAG}
                        value={props?.flagContent}
                        disabled={props?.flagContent}
                        maxLength="1000"
                        rows="2"
                        required
                    />
                )}
                <hr className="my-4 bg-neutral-50" />
            </div>
            <div className="pendo-ignore relative grid grid-cols-[1fr_4fr] gap-1 pr-8 text-sm">
                <div
                    onClick={handleCopy}
                    className="absolute right-0 cursor-pointer"
                >
                    <CopyIcon isCopied={copied} />
                </div>
                <span className="font-medium">{USER_ID}</span>
                <span id="sidebar-user-uuid">{props.userData.uuid}</span>
                <span className="font-medium">
                    {PATIENT_INFO_HEADER.PATIENT_ID}
                </span>
                <span id="sidebar-patient-uuid">{patientData.PatientUUID}</span>
                <span className="font-medium">
                    {PATIENT_INFO_HEADER.PLAN_ID}
                </span>
                <span id="sidebar-plan-uuid">{headerData.PlanUUID}</span>
                <span className="font-medium">
                    {PATIENT_INFO_HEADER.VISIT_DATE}
                </span>
                <span id="sidebar-visit-date">{visitData}</span>
            </div>
            {props.showBiopsyTemplateSelector && isAvendaUser && (
                <>
                    <hr className="my-4 bg-neutral-50" />
                    <div>
                        <p className="text-xl">
                            {PLAN_DETAILS_HEADERS.UPDATE_BIOPSY_TEMPLATE}
                        </p>
                        <Dropdown
                            id="sidebar-biopsy-template"
                            placeholder={
                                PLAN_DETAILS_PLACEHOLDERS.SELECT_BIOPSY_TEMPLATE
                            }
                            dropdownItems={SystematicTemplateOptions}
                            value={getBxTemplate(treatmentPlan)}
                            onChange={handleBiopsyTemplateChange}
                        />
                    </div>
                    {pendingBiopsyTemplate && (
                        <DecisionAlertCard
                            id="change-bx-template-alert"
                            overlayClassName="fixed inset-0 z-10 flex items-center justify-center bg-black/50"
                            className="tw-rounded-sm"
                            noBtnTxt={CHANGE_BIOPSY_TEMPLATE_PROMPT.CANCEL_BTN}
                            noBtnCallback={() =>
                                handleBiopsyTemplateChange(null)
                            }
                            yesBtnTxt={
                                CHANGE_BIOPSY_TEMPLATE_PROMPT.CONFIRM_BTN
                            }
                            yesBtnCallback={confirmBiopsyTemplateChange}
                            style={{
                                position: "relative",
                                top: "auto",
                                left: "auto",
                                backgroundColor: "white",
                            }}
                        >
                            <div className="w-full px-6 pt-6 text-neutral-800">
                                <p className="text-2xl font-semibold">
                                    {CHANGE_BIOPSY_TEMPLATE_PROMPT.HEADER}
                                </p>
                                <p className="mt-3">
                                    {CHANGE_BIOPSY_TEMPLATE_PROMPT.BODY}
                                </p>
                            </div>
                        </DecisionAlertCard>
                    )}
                </>
            )}
        </div>
    );
}

PlanDetailsTab.propTypes = {
    doctorList: PropTypes.array,
    authToken: PropTypes.string,
    userData: PropTypes.object,
    currentTreatmentPlan: PropTypes.object,
    handleTextAreaChange: PropTypes.func,
    hasFlag: PropTypes.bool,
    flagContent: PropTypes.string,
    toggleFlag: PropTypes.func,
    showBiopsyTemplateSelector: PropTypes.bool,
};

export default PlanDetailsTab;
